import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from '../config/api';

const HomePage = ({ user, isSecretary }) => {
  const navigate = useNavigate();

  // All state hooks declared unconditionally at top level
  const [society, setSociety] = useState(user.society || null);
  const [loading, setLoading] = useState(!user.society && isSecretary);
  const [inspectorSocieties, setInspectorSocieties] = useState([]);
  const [loadingSocieties, setLoadingSocieties] = useState(!isSecretary);

  // Fetch Secretary society details if needed
  useEffect(() => {
    if (isSecretary && !user.society) {
      const fetchSociety = async () => {
        try {
          const res = await fetch(`${API_BASE_URL}/api/societies?email=${encodeURIComponent(user.email || '')}&phone=${encodeURIComponent(user.phone || '')}&name=${encodeURIComponent(user.name || '')}`);
          const data = await res.json();
          if (data.success && data.society) {
            setSociety(data.society);
          }
        } catch (err) {
          console.error('Error fetching society details:', err);
        } finally {
          setLoading(false);
        }
      };
      fetchSociety();
    }
  }, [isSecretary, user]);

  // Fetch Inspector or AR assigned societies count if needed
  useEffect(() => {
    if (!isSecretary) {
      const fetchRoleSocieties = async () => {
        try {
          const userRole = (user.role || '').toLowerCase();
          const endpoint = userRole === 'ar'
            ? `${API_BASE_URL}/api/societies/ar?email=${encodeURIComponent(user.email || '')}&phone=${encodeURIComponent(user.phone || '')}&name=${encodeURIComponent(user.name || '')}`
            : `${API_BASE_URL}/api/societies/inspector?email=${encodeURIComponent(user.email || '')}&phone=${encodeURIComponent(user.phone || '')}&name=${encodeURIComponent(user.name || '')}`;
          
          const res = await fetch(endpoint);
          const data = await res.json();
          if (data.success && Array.isArray(data.societies)) {
            setInspectorSocieties(data.societies);
          }
        } catch (err) {
          console.error('Error fetching role societies:', err);
        } finally {
          setLoadingSocieties(false);
        }
      };
      fetchRoleSocieties();
    }
  }, [isSecretary, user]);

  const formattedUserName = (user.name || '').replace(/\b\w/g, l => l.toUpperCase());

  // Render Secretary View
  if (isSecretary) {
    if (loading) {
      return (
        <section className="card">
          <p>Loading Society details...</p>
        </section>
      );
    }

    const soc = society || {};
    const societyName = soc.SOCIETY || user.societyName || 'N/A';
    const division = soc.DIVISION || user.division || 'N/A';
    const district = soc.DISTRICT || user.district || 'N/A';
    const arCircle = soc.AR || soc.ARNAME || user.arCircle || 'N/A';
    const village = soc.VILLAGE || user.village || 'N/A';
    const inspectorName = soc.INSPECTOR || 'N/A';
    const inspectorPhone = soc.INSPECTORMOBILE ? ` (${soc.INSPECTORMOBILE})` : '';
    const inspectorInfo = inspectorName !== 'N/A' ? `${inspectorName}${inspectorPhone}` : 'N/A';
    const roleDisplay = (user.role || 'Secretary').replace(/\b\w/g, l => l.toUpperCase());

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#1b2559', margin: 0, textTransform: 'capitalize' }}>
          Welcome, {formattedUserName} <span style={{ fontSize: '13px', background: '#e0e7ff', color: '#4318ff', padding: '4px 10px', borderRadius: '12px', fontWeight: '600', marginLeft: '8px', verticalAlign: 'middle', textTransform: 'none' }}>{roleDisplay}</span>
        </h2>

        <div className="cards-grid">
          {/* Left Card: Society Information */}
          <div className="info-card">
            <div className="info-card-header">
              <span className="card-icon">🏛️</span>
              <h3>{societyName}</h3>
            </div>
            <div className="info-details">
              <p><strong>Division :</strong> {division}</p>
              <p><strong>District :</strong> {district}</p>
              <p><strong>AR Circle:</strong> {arCircle}</p>
              <p><strong>Village :</strong> {village}</p>
              <p><strong>Inspector :</strong> {inspectorInfo}</p>
            </div>
          </div>

          {/* Right Card: Secretary Profile */}
          <div className="info-card">
            <div className="info-card-header">
              <span className="card-icon">👤</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <h3 style={{ margin: 0 }}>{soc.SECRETARY || user.name}</h3>
                <span style={{ fontSize: '12px', background: '#e0e7ff', color: '#4318ff', padding: '3px 8px', borderRadius: '10px', fontWeight: '600' }}>{roleDisplay}</span>
              </div>
            </div>
            <div className="info-details">
              <p><strong>Mobile No :</strong> {soc.SECRETARYMOBILE || user.phone}</p>
              <p><strong>Login ID :</strong> {soc.SECRETARYEMAILID || user.email}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const userRole = (user.role || '').toLowerCase();
  const isPowerRole = userRole === 'power';
  const isRCSRole = userRole === 'rcs';
  const roleDisplay = (user.role || (isPowerRole ? 'Power' : 'Inspector')).replace(/\b\w/g, l => l.toUpperCase());

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#1b2559', margin: 0, textTransform: 'capitalize' }}>
        Welcome, {formattedUserName}
      </h2>

      <div className="cards-grid">
        {/* Profile Card */}
        <div className="info-card">
          <div className="info-card-header">
            <span className="card-icon">👤</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <h3 style={{ margin: 0 }}>{formattedUserName}</h3>
              <span style={{ fontSize: '12px', background: isPowerRole ? '#fef3c7' : '#e0e7ff', color: isPowerRole ? '#d97706' : '#4318ff', padding: '3px 8px', borderRadius: '10px', fontWeight: '600' }}>{roleDisplay}</span>
            </div>
          </div>
          <div className="info-details">
            <p><strong>Mobile No :</strong> {user.phone || 'N/A'}</p>
            <p><strong>Login ID :</strong> {user.email || 'N/A'}</p>
            {!isPowerRole && !isRCSRole && <p><strong>Assigned Societies :</strong> {inspectorSocieties.length}</p>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
